/** @format */

import { FC, useCallback, useEffect, useRef, useState } from "react";
import { EventInfo, ClassicEditor } from "ckeditor5";
import { Comments, TrackChangesData } from "ckeditor5-premium-features";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import "ckeditor5/ckeditor5.css";
import "@edres/ckeditor5-glossary/index.css";
/** This CSS was causing "start value has mixed support" warning so duplicated and fixed CSS in 2 places. start to flex-start. */
// import 'ckeditor5-premium-features/ckeditor5-premium-features.css';
import "./ckeditor5-premium-features.scss";
import "./VisualEditor.scss";
import { useAppSelector } from "../../app/hooks";
import { makePremiumConfig, makeBasicConfig } from "./PluginConfigs";
import BLSpinner from "../../assets/animations/BLSpinner/BLSpinner";
import { EDITOR_HEADER_ID } from "../contentEditor/contentEditorHeader";

export type VisualEditorProps = {
  data?: string;
  editorId?: string;
  onReady?: (editor: ClassicEditor) => void;
  onChange?: (event: EventInfo<string, unknown>, editor: ClassicEditor) => void;
  onBlur?: (event: EventInfo<string, unknown>, editor: ClassicEditor) => void;
  onFocus?: (event: EventInfo<string, unknown>, editor: ClassicEditor) => void;
  forcePlainEditor?: boolean;
};

const VisualEditor: FC<VisualEditorProps> = ({
  data,
  editorId,
  onReady,
  onChange,
  onBlur,
  onFocus,
}) => {
  const editorPresenceRef = useRef(null);
  const editorRef = useRef(null);
  const editorAnnotationsRef = useRef<HTMLDivElement | null>(null);
  const editorInstanceRef = useRef<ClassicEditor | null>(null);

  const isStaff = useAppSelector((state) => state.auth.isStaff);

  const [isLayoutReady, setIsLayoutReady] = useState<boolean>(false);
  const [isEditorReady, setIsEditorReady] = useState<boolean>(false);

  // Change Annotation UI's (Anotation === comments) depending on window size
  const refreshDisplayMode = useCallback((editor?: ClassicEditor) => {
    // HACK: fixes ckEditor sticky panel not resizing with window.
    //       @see https://github.com/ckeditor/ckeditor5/issues/5469
    const ckEditorStickyHeader = document.querySelector(
      ".ck-sticky-panel__content_sticky",
    ) as HTMLElement;
    if (ckEditorStickyHeader) {
      // keep the header the same size as the editor
      ckEditorStickyHeader.style.width =
        (document.querySelector(".ck-editor__top")?.clientWidth.toString() ??
          "") + "px";

      // keep the header stuck to below the modal header
      ckEditorStickyHeader.style.top =
        (document
          .getElementById(EDITOR_HEADER_ID)
          ?.getBoundingClientRect()
          .bottom.toString() ?? "") + "px";
    }

    if (
      (!editorInstanceRef?.current && !editor) ||
      !editorAnnotationsRef?.current
    ) {
      return;
    }

    const editorInstance = editor ?? editorInstanceRef.current;
    if (!editorInstance || !editorInstance.plugins.has("AnnotationsUIs"))
      return;

    const annotationsUIs = editorInstance.plugins.get("AnnotationsUIs");
    let commentsCount: number | undefined;

    if (editorInstance.plugins.has("CommentsRepository")) {
      const comments = editorInstance.plugins.get("CommentsRepository");
      // Fetch all comments that aren't:
      // archived / resolved / un-attached
      commentsCount =
        comments
          .getCommentThreads()
          .filter(
            (comment) =>
              !comment.isResolved &&
              comment.isAttached &&
              comment.archivedAt === null &&
              comment.comments.length > 0,
          )?.length ?? 0;
    }

    const sidebarElement = editorAnnotationsRef.current.parentElement;

    if (!sidebarElement) return;

    if (
      window.innerWidth < 1070 ||
      (commentsCount !== undefined && commentsCount <= 0)
    ) {
      sidebarElement.classList.remove("narrow");
      sidebarElement.classList.add("hidden");
      annotationsUIs.switchTo("inline");
    } else if (window.innerWidth < 1300) {
      // Narrow-sidebar: comments in sidebar and others inline
      sidebarElement.classList.remove("hidden");
      sidebarElement.classList.add("narrow");

      annotationsUIs.deactivateAll();
      annotationsUIs.activate(
        "narrowSidebar",
        // @ts-ignore
        (annotation) => annotation.type === "comment",
      );
      annotationsUIs.activate(
        "inline",
        // @ts-ignore
        (annotation) => annotation.type !== "comment",
      );
    } else {
      // Wide-sidebar: comments in sidebar and others inline
      sidebarElement.classList.remove("hidden", "narrow");
      annotationsUIs.deactivateAll();
      annotationsUIs.activate(
        "wideSidebar",
        // @ts-ignore
        (annotation) => annotation.type === "comment",
      );
      annotationsUIs.activate(
        "inline",
        // @ts-ignore
        (annotation) => annotation.type !== "comment",
      );
    }
  }, []);

  // Prevent CKEditor from firing requests after unloaded
  const checkPendingActions = useCallback((domEvt: Event) => {
    if (!editorInstanceRef?.current) {
      return;
    }

    const editorInstance = editorInstanceRef.current;

    if (
      editorInstance.plugins.has("PendingActions") &&
      editorInstance.plugins.get("PendingActions").hasAny
    ) {
      domEvt.preventDefault();
    }
  }, []);

  // Component has mounted check
  useEffect(() => {
    setIsLayoutReady(true);

    return () => {
      setIsLayoutReady(false);
      setIsEditorReady(false);
    };
  }, [isLayoutReady]);

  // Function to watch for comment events being fired (e.g. Adding comments)
  const setupListeners = useCallback(
    (editor: ClassicEditor) => {
      if (editor && editorId) {
        const editorInstance = editor;
        if (editorInstance.plugins.has("CommentsRepository")) {
          const comments = editorInstance.plugins.get("CommentsRepository");

          comments.on(
            `resolveCommentThread:${editorId}`,
            () => refreshDisplayMode,
          );
          comments.on(
            `submitCommentThread:${editorId}`,
            () => refreshDisplayMode,
          );

          // TODO: Use listener below to create mention notifications
          /* comments.on(`addComment:${editorId}`, ( evt, data ) => {
          console.log( evt, data );
        }); */

          comments.on(
            `removeCommentThread:${editorId}`,
            () => refreshDisplayMode,
          );
          comments.on(
            `updateCommentTbread:${editorId}`,
            () => refreshDisplayMode,
          );
        }
      }
    },
    [editorId, refreshDisplayMode],
  );

  // Function to setup track changes plugin on CKEditor
  const setupTrackChanges = useCallback((editor: ClassicEditor) => {
    if (editor) {
      const editorInstance = editor;

      if (editorInstance.plugins.has("TrackChangesEditing")) {
        const trackChangesEditing = editorInstance.plugins.get(
          "TrackChangesEditing",
        );

        if (trackChangesEditing.trackChangesCommand.value !== true) {
          // Enable glossary terms components
          trackChangesEditing.enableDefaultAttributesIntegration("glossary");
          trackChangesEditing.registerInlineAttribute("glossaryDefinition");
          trackChangesEditing.enableCommand("removeglossary");

          // Enable MathType and ChemType
          trackChangesEditing.enableCommand("MathType");
          trackChangesEditing.enableCommand("ChemType");
        }

        // Set track changes off by default
        trackChangesEditing.trackChangesCommand.value = false;
      }
    }
  }, []);

  const onEditorReady = useCallback(
    (editor: ClassicEditor) => {
      editorInstanceRef.current = editor;

      refreshDisplayMode(editor);

      window.addEventListener("resize", () => refreshDisplayMode(editor));
      window.addEventListener("beforeunload", checkPendingActions);

      setupListeners(editor);
      setupTrackChanges(editor);

      setIsEditorReady(true);
    },
    [
      setupListeners,
      setupTrackChanges,
      refreshDisplayMode,
      checkPendingActions,
    ],
  );

  return (
    <>
      <div className="m-3 text-center">
        {!isEditorReady && <BLSpinner size={"1.5rem"} />}
      </div>

      {/* render but hide the editor while it is still loading */}
      <div className={`main-container ${isEditorReady ? "" : "d-none"}`}>
        <div className="presence mb-2" ref={editorPresenceRef}></div>
        <div className="editor-container editor-container_classic-editor editor-container_include-annotations">
          <div className="editor-container__editor-wrapper">
            <div className="editor-container__editor">
              <div ref={editorRef}>
                {/* wait for component to mount */}
                {isLayoutReady && (
                  <CKEditor
                    editor={ClassicEditor}
                    data={data}
                    config={
                      isStaff
                        ? makePremiumConfig(
                            editorAnnotationsRef,
                            editorId,
                            editorPresenceRef,
                          )
                        : makeBasicConfig()
                    }
                    onReady={(editor) => {
                      onEditorReady(editor);
                      onReady && onReady(editor);
                    }}
                    onChange={(event, editor) => {
                      onChange && onChange(event, editor);
                    }}
                    onBlur={(event, editor) => {
                      onBlur && onBlur(event, editor);
                    }}
                    onFocus={(event, editor) => {
                      onFocus && onFocus(event, editor);
                    }}
                  />
                )}
              </div>
            </div>
            <div className="editor-container__sidebar hidden">
              <div ref={editorAnnotationsRef}></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const getDataWithoutCollaboration = async (
  editor: ClassicEditor,
): Promise<string> => {
  let data: string;
  if (editor.plugins.has(TrackChangesData)) {
    try {
      const content = await (
        editor.plugins.get("TrackChangesData") as TrackChangesData
      ).getDataWithDiscardedSuggestions({ ignoreResolvedComments: true });
      data =
        typeof content === "string"
          ? content
          : Object.values(content).join(" ");
    } catch {
      console.error(
        "Failed to fetch data with discarded suggestions. Using getData instead.",
      );
      data = editor.getData({ ignoreResolvedComments: true });
    }
  } else {
    data = editor.getData({ ignoreResolvedComments: true });
  }

  if (editor.plugins.has(Comments) && data) {
    data = data.replace(
      /<comment-.*?.*?(?:\/)?>(.*?)<\/comment-.*?>|data-comment-.*?(["].*?["]|['].*?[']|[`].*?[`])/g,
      "",
    );
  }

  return data;
};

export default VisualEditor;
